/**
 * Toast - Cross-platform toast notification
 * - Web: render as a snackbar at bottom of screen
 * - Native: Alert.alert fallback (or could use ToastAndroid)
 */
import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { Icon } from './Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

export interface ToastProps {
  visible: boolean;
  message: string;
  variant?: 'success' | 'error' | 'info';
  onHide?: () => void;
  duration?: number;
}

export const Toast: React.FC<ToastProps> = ({
  visible,
  message,
  variant = 'success',
  onHide,
  duration = 2200,
}) => {
  const [shown, setShown] = useState(visible);

  useEffect(() => {
    setShown(visible);
    if (visible && duration > 0) {
      const timer = setTimeout(() => {
        setShown(false);
        onHide?.();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [visible, duration, onHide]);

  if (!shown) return null;

  const bg = variant === 'error' ? '#FEE2E2' : variant === 'info' ? '#DBEAFE' : '#D1FAE5';
  const text = variant === 'error' ? '#991B1B' : variant === 'info' ? '#1E40AF' : '#065F46';
  const iconName = variant === 'error' ? 'error' : variant === 'info' ? 'info' : 'check_circle';
  const iconColor = variant === 'error' ? '#DC2626' : variant === 'info' ? '#2563EB' : '#00A651';

  return (
    <View style={styles.toast} pointerEvents="none">
      <View style={[styles.toastBox, { backgroundColor: bg }]}>
        <Icon name={iconName as any} size={20} color={iconColor} />
        <Text style={[styles.toastText, { color: text }]}>{message}</Text>
      </View>
    </View>
  );
};

// Simple Toast manager hook
export function useToast() {
  const [toast, setToast] = useState<{ visible: boolean; message: string; variant?: 'success' | 'error' | 'info' }>({
    visible: false,
    message: '',
  });

  const showToast = (message: string, variant: 'success' | 'error' | 'info' = 'success') => {
    setToast({ visible: true, message, variant });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, visible: false }));
  };

  return {
    toastProps: toast,
    showToast,
    hideToast,
  };
}

const styles = StyleSheet.create({
  toast: {
    position: 'absolute',
    bottom: spacing[8],
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 9999,
  },
  toastBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[3],
    borderRadius: borderRadius.md,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 6,
    maxWidth: '90%',
  },
  toastText: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
  },
});